import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { FilterOption, InitialsPipe } from 'shared';
import {
  DropdownCoordinationService,
  getNextOptionIndex,
  getPreviousOptionIndex,
} from '../dropdown-coordination.service';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { AvatarComponent } from '../avatar/avatar.component';

/**
 * Generic multi-select filter dropdown, driven by a `TableFilterFieldConfig`'s derived
 * `FilterOption[]` — used for the Tasks/Members table filter bars (Status, Priority, Assignee...).
 */
@Component({
  selector: 'ocp-table-filter-dropdown',
  imports: [TranslatePipe, HlmPopoverImports, ThemedIconComponent, AvatarComponent, InitialsPipe],
  templateUrl: './table-filter-dropdown.component.html',
  styleUrl: './table-filter-dropdown.component.scss',
  host: {
    class: 'table-filter-dropdown',
  },
})
export class TableFilterDropdownComponent {
  private readonly document = inject(DOCUMENT);
  private readonly dropdownCoordination = inject(DropdownCoordinationService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly instanceId = this.dropdownCoordination.generateInstanceId();

  /** Already-translated label shown on the trigger button (e.g. "Status"). */
  readonly label = input.required<string>();
  /** Already-translated label for the "Show all" (clear) row. */
  readonly showAllLabel = input.required<string>();
  readonly options = input<FilterOption[]>([]);
  readonly selected = input<ReadonlySet<string>>(new Set());

  /** Emits the option value that was toggled on/off. */
  readonly toggleOption = output<string>();
  /** Emits when "Show all" is activated, clearing every selection for this field. */
  readonly clearAll = output<void>();

  protected readonly isOpen = signal(false);
  protected readonly overlayState = signal<'open' | 'closed' | null>(null);
  protected readonly triggerWidth = signal<string>('auto');
  protected readonly focusedIndex = signal<number>(-1);
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  protected readonly selectedCount = computed(() => this.selected().size);

  constructor() {
    this.dropdownCoordination.registerCloseHandler(
      this.elementRef,
      () => this.closePopover(),
      this.destroyRef
    );
  }

  protected onTriggerClick(): void {
    this.dropdownCoordination.notifyOpen(this.elementRef);
  }

  protected onStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.overlayState.set(null);
    }
    this.isOpen.set(state === 'open');
    if (state === 'open') {
      const triggerWidth = this.trigger()?.nativeElement.offsetWidth;
      this.triggerWidth.set(triggerWidth ? `${triggerWidth}px` : 'auto');
      this.focusedIndex.set(0);
    } else {
      this.focusedIndex.set(-1);
    }
  }

  private closePopover(): void {
    this.overlayState.set('closed');
  }

  protected isSelected(option: FilterOption): boolean {
    return this.selected().has(String(option.value));
  }

  protected onShowAllClick(event: Event): void {
    event.stopPropagation();
    this.clearAll.emit();
  }

  protected onOptionClick(option: FilterOption, event: Event): void {
    event.stopPropagation();
    this.toggleOption.emit(String(option.value));
  }

  /** `0` is the "Show all" row; `index + 1` maps to `options()[index]`. */
  protected onRowKeydown(event: KeyboardEvent, rowIndex: number): void {
    const totalRows = this.options().length + 1;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (rowIndex === 0) {
          this.clearAll.emit();
        } else {
          this.toggleOption.emit(String(this.options()[rowIndex - 1].value));
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusRow(getNextOptionIndex(rowIndex, totalRows));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusRow(getPreviousOptionIndex(rowIndex, totalRows));
        break;
      case 'Escape':
        event.preventDefault();
        this.closePopover();
        this.focusedIndex.set(-1);
        break;
    }
  }

  protected getRowId(rowIndex: number): string {
    return `table-filter-dropdown-row-${this.instanceId}-${rowIndex}`;
  }

  protected trackByValue(_index: number, option: FilterOption): string {
    return String(option.value);
  }

  private focusRow(rowIndex: number): void {
    this.focusedIndex.set(rowIndex);
    afterNextRender(() => this.document.getElementById(this.getRowId(rowIndex))?.focus(), {
      injector: this.injector,
    });
  }
}
